import React from 'react';
import { IProduct } from '../../Api/Models/Products';
import { useLocalization } from '../Helpers/useLocalization';
import CurrencyFormat from '../Helpers/CurrencyFormat';

interface IPromoPriceProps {
    product?: IProduct;
    // False when the shopper cannot see pricing at all, in which case there is no price to qualify.
    canSeePricing?: boolean;
    // 'full' stacks the badge, the original price and the end date on their own lines — the product
    // page, where there is vertical room. 'short' is the compact card form: badge and struck price on
    // ONE line, no labels, no end date. Cards are the tightest space on the storefront and every extra
    // line there pushes the title into a clip.
    variant?: 'full' | 'short';
    // Renders ONE piece instead of the whole block, so a caller can place them separately — the card
    // puts the pill beside the price and the struck price on the line below it. The "did a promo win"
    // guard still lives here, so a caller can never render half a promo that never applied.
    part?: 'badge' | 'was';
    className?: string;
    style?: React.CSSProperties;
    // Merchant styling from productPageConfig. Only the 'full' variant takes these: the card form is
    // laid out by the card's own config, and letting product-page styles reach into it would style two
    // different components from one key.
    badgeStyle?: React.CSSProperties;
    originalPriceStyle?: React.CSSProperties;
    endDateStyle?: React.CSSProperties;
}

// The promo badge, the pre-promo ("was") price, and when the promo ends.
//
// Renders nothing unless the API said a promo actually priced this product. It decides that: whether a
// promo applies, which one won, what the pre-promo price was and when it ends are all resolved
// server-side, because the price the shopper is charged comes from the same resolution. Recomputing any
// of it here would let the badge disagree with the number beside it, which is the class of bug AC-986 was.
//
// Deliberately no arithmetic: no percentage is computed from price/originalPrice. A promo may be a
// markup override OR a discount off the calculated price, applied to a live vendor cost, so a "% off"
// derived here would drift from what the merchant configured as costs move.
//
// The end date is likewise never counted down or compared against `now`. A promo that has passed its
// end date simply stops pricing server-side and this component stops rendering; a client that hid the
// badge on its own would disagree with the price still being charged from a cached response.
const PromoPrice: React.FC<IPromoPriceProps> = ({
    product, canSeePricing, variant = 'full', part, className, style,
    badgeStyle, originalPriceStyle, endDateStyle,
}) => {
    const L = useLocalization();
    const promoName = product?.promoName;
    const originalPrice = product?.originalPrice;

    // The BFF already nulls these alongside `price` when pricing is hidden; checking again here keeps
    // the component honest on its own terms rather than relying on the caller having passed the right flag.
    if (!canSeePricing || product?.hidePrice) return null;
    if (!promoName) return null;

    const showWas = typeof originalPrice === 'number'
        && typeof product?.price === 'number'
        && originalPrice > product.price;

    // Date only, in the shopper's locale. A promo ending "Sept 1" stops at the first instant of Sept 1
    // (the window is half-open server-side), so the date is the honest unit — a time would imply a
    // precision the 30-minute price cache in front of this cannot honor.
    const endsAt = product?.promoEndsAt ? new Date(product.promoEndsAt) : undefined;
    const showEnds = variant === 'full' && endsAt !== undefined && !Number.isNaN(endsAt.getTime());

    const badge = <span className='product-promo-badge' style={badgeStyle}>{promoName}</span>;

    // Single-piece rendering. Returns null rather than an empty wrapper when the requested piece has
    // nothing to show, so a caller's flex gap never opens around it.
    if (part === 'badge') return <span className={className} style={style}>{badge}</span>;
    if (part === 'was') {
        if (!showWas) return null;
        return (
            <span className={`product-promo-was ${className ?? ''}`.trim()} style={style} title={L('localization_promo_was_price')}>
                <CurrencyFormat value={originalPrice!} strikeThrough />
            </span>
        );
    }

    // One line, no labels: a card has no room for "Original Price:" and the struck number beside the
    // badge reads correctly without it.
    if (variant === 'short') {
        return (
            <span className={className} style={{ display: 'block', ...style }}>
                {badge}
                {showWas && (
                    <span className='product-promo-was' title={L('localization_promo_was_price')}>
                        <CurrencyFormat value={originalPrice!} strikeThrough />
                    </span>
                )}
            </span>
        );
    }

    // Stacked, each on its own line and each independently styleable, so the block reads top to bottom:
    // what the promo is, what it replaced, when it ends.
    return (
        <span className={className} style={{ display: 'block', ...style }}>
            <span className='product-promo-badge-line' style={{ display: 'block' }}>{badge}</span>
            {showWas && (
                <span className='product-promo-was' style={{ display: 'block', ...originalPriceStyle }}>
                    {L('localization_promo_was_price')}: <CurrencyFormat value={originalPrice!} strikeThrough />
                </span>
            )}
            {showEnds && (
                <span className='product-promo-ends' style={{ display: 'block', ...endDateStyle }}>
                    {L('localization_promo_ends')} {endsAt!.toLocaleDateString()}
                </span>
            )}
        </span>
    );
};

export default PromoPrice;
